import { DateTime } from 'luxon';

import {
  BidStrategy,
  CreateMerchantAdCampaignRequest,
  CreateVenueAdCampaignRequest,
  PatchVenueAdCampaignRequest,
} from 'apis/ad-bookings/types';
import { convertDecimalToMonetaryInt } from 'common/utils/currencyDecimals';

import { AdCampaignFormValues, MerchantAdCampaignFormValues, VenueAdCampaignFormValues } from '../../types';

export const transformCreateVenueAdCampaignRequest = (
  values: VenueAdCampaignFormValues,
  currency: Currency,
  country: Country,
): CreateVenueAdCampaignRequest => ({
  country,
  start_date: formatDate(values.startDate),
  end_date: getEndDate(values.scheduleStrategy, values.endDate),
  weekly_budget_amount: convertDecimalToMonetaryInt(Number(values.budgetAmount), currency),
  bid_amount: getBidAmount(values.bidStrategy, values.customBidAmount, currency),
  bid_strategy: getBidStrategy(values.bidStrategy),
  consent: values.agreementTermsAndConditions,
});

export const transformCreateMerchantAdCampaignRequest = (
  values: MerchantAdCampaignFormValues,
  merchantId: string,
  currency: Currency,
  country: Country,
): CreateMerchantAdCampaignRequest => ({
  country,
  merchant_id: merchantId,
  venue_ids: values.venueIds,
  start_date: formatDate(values.startDate),
  end_date: getEndDate(values.scheduleStrategy, values.endDate),
  weekly_budget_amount: convertDecimalToMonetaryInt(Number(values.budgetAmount), currency),
  bid_amount: getBidAmount(values.bidStrategy, values.customBidAmount, currency),
  bid_strategy: getBidStrategy(values.bidStrategy),
});

export const transformUpdateVenueAdCampaignRequest = (
  values: AdCampaignFormValues,
  currency: Currency,
): PatchVenueAdCampaignRequest => ({
  start_date: values.startDate ? formatDate(values.startDate) : undefined,
  end_date: values.endDate ? formatDate(values.endDate) : undefined,
  weekly_budget_amount: values.budgetAmount
    ? convertDecimalToMonetaryInt(Number(values.budgetAmount), currency)
    : undefined,
  bid_amount:
    values.bidStrategy === 'custom' ? convertDecimalToMonetaryInt(Number(values.customBidAmount), currency) : undefined,
  bid_strategy: values.bidStrategy ? getBidStrategy(values.bidStrategy) : undefined,
  always_on: values.scheduleStrategy === 'alwaysOn' ? true : undefined,
});

const formatDate = (date: string) => DateTime.fromISO(date).toFormat('yyyy-MM-dd');

const getEndDate = (scheduleStrategy: string, endDate: string | undefined): string | null =>
  scheduleStrategy === 'selectDates' && endDate ? formatDate(endDate) : null;

const getBidAmount = (
  bidStrategy: string,
  customBidAmount: number | undefined,
  currency: Currency,
): number | undefined =>
  bidStrategy === 'custom' && customBidAmount
    ? convertDecimalToMonetaryInt(Number(customBidAmount), currency)
    : undefined;

const getBidStrategy = (bidStrategy: string): BidStrategy => (bidStrategy === 'custom' ? 'CUSTOM' : 'AUTOMATIC');
